import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
} from '@mui/material';
import { EmailOutlined, ArrowBack, CheckCircleOutline } from '@mui/icons-material';
import apiClient from '../services/api/apiClient';

const GEO_PATTERN = `
  radial-gradient(circle at 15% 50%, rgba(255,255,255,0.04) 0%, transparent 50%),
  radial-gradient(circle at 85% 20%, rgba(255,255,255,0.05) 0%, transparent 40%),
  radial-gradient(circle at 60% 80%, rgba(255,255,255,0.03) 0%, transparent 35%)
`.trim();

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await apiClient.post('/admin/forgot-password/', { email });
      setSent(true);
    } catch (err: any) {
      // Show generic error so we don't leak info
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f9fafb',
      transition: 'all 0.25s ease',
      '& fieldset': { borderColor: '#d1d5db' },
      '&:hover fieldset': { borderColor: '#2563eb' },
      '&.Mui-focused fieldset': {
        borderColor: '#1e3a8a',
        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `${GEO_PATTERN}, linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #1e40af 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative rings */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          width: 480, height: 480,
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '50%', top: -120, right: -120,
        },
        '&::after': {
          content: '""', position: 'absolute',
          width: 320, height: 320,
          border: '1px solid rgba(255,255,255,0.04)',
          borderRadius: '50%', bottom: -80, left: -80,
        },
      }} />

      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          p: 5,
          borderRadius: 3,
          boxShadow: '0 25px 70px rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.08)',
          position: 'relative',
          zIndex: 1,
          mx: 2,
          animation: 'fadeInUp 0.5s ease-out',
          '@keyframes fadeInUp': {
            from: { opacity: 0, transform: 'translateY(24px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* Back to login */}
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/login')}
            size="small"
            sx={{ color: '#6b7280', textTransform: 'none', fontWeight: 600, pl: 0 }}
          >
            Back to Sign In
          </Button>
        </Box>

        {/* Logo + Brand */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <Box
              component="img"
              src="/images/logo.png"
              alt="Daily2K"
              sx={{ height: 44, width: 'auto', objectFit: 'contain' }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e3a8a', letterSpacing: '-0.3px' }}>
            Daily2K Admin
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {sent ? (
          /* ── Success state ── */
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleOutline sx={{ fontSize: 56, color: '#10b981', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Check Your Email
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, lineHeight: 1.7 }}>
              If <strong>{email}</strong> is registered as an admin account, we've sent a
              password reset link. It expires in 24 hours.
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 3 }}>
              Didn't receive it? Check your spam folder, or{' '}
              <Box
                component="span"
                onClick={() => setSent(false)}
                sx={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >
                try again
              </Box>.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                py: 1.3,
                fontWeight: 700,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
              }}
            >
              Return to Sign In
            </Button>
          </Box>
        ) : (
          /* ── Form state ── */
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Forgot Your Password?
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Enter your admin email and we'll send you a reset link.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', display: 'block', mb: 0.5, fontSize: '0.8rem', letterSpacing: 0.3 }}>
                  EMAIL ADDRESS
                </Typography>
                <TextField
                  fullWidth
                  placeholder="admin@daily2k.ng"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                  variant="outlined"
                  size="small"
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ fontSize: 18, color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={isLoading || !email}
                sx={{
                  py: 1.4,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  boxShadow: '0 4px 14px rgba(30, 64, 175, 0.35)',
                  '&:hover:not(:disabled)': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    boxShadow: '0 8px 20px rgba(30, 64, 175, 0.45)',
                    transform: 'translateY(-1px)',
                  },
                  '&.Mui-disabled': {
                    background: 'linear-gradient(135deg, #93c5fd 0%, #bfdbfe 100%)',
                    color: '#fff',
                  },
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: 'white' }} />
                    <span>Sending…</span>
                  </Box>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          </>
        )}
      </Card>
    </Box>
  );
}
